import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

export const TENURES = ['Owned', 'Rented', 'Devised', 'Life Estate', 'Unknown'] as const;
export type Tenure = (typeof TENURES)[number];

/** A property as the client page lists it. `occupied` is derived, never stored. */
export interface ClientProperty {
  id: string;
  property_type: string | null;
  property_type_label: string | null;
  tenure: Tenure;
  disposition: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  neighbourhood: string | null;
  neighbourhood_source: 'lookup' | 'person' | null;
  discharged: boolean;
  discharged_on: string | null;
  occupied: boolean;
}

export interface PropertyType {
  code: string;
  label: string;
  residence_noun: string | null;
}

export interface ClientProperties {
  /** False for a role with no property permission at all: the panel is not shown. */
  may_read: boolean;
  may_record: boolean;
  properties: ClientProperty[];
  property_types: PropertyType[];
  tenures: Tenure[];
}

export interface NewProperty {
  property_type: string;
  tenure: Tenure;
  street_address: string;
  unit?: string;
  city: string;
  state: string;
  zip?: string;
  county?: string;
  neighbourhood?: string;
  parcel_id?: string;
  legal_description?: string;
  lis_pendens_filed: boolean;
  court_auth_required: boolean;
  notes?: string;
  /** 122: when the client came to hold it, only if after the appointment. */
  held_since?: string;
}

export interface PropertyAdded {
  property_id: string;
  site_visits_open_before: number;
  site_visits_open_after: number;
}

export function useClientProperties(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'properties'],
    queryFn: () => apiFetch<ClientProperties>(`/clients/${clientId}/properties`, getToken),
    ...queryDefaults,
  });
}

export function useAddProperty(clientId: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NewProperty) =>
      apiFetch<PropertyAdded>(`/clients/${clientId}/properties`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}

export type RealEstateView = 'active' | 'inactive';

/** A row of the Real Estate list. `occupied` and `client_deceased` are derived, never stored. */
export interface RealEstateRow {
  id: string;
  client_id: string;
  client_ref: string;
  display_name: string;
  legal_status: string;
  client_deceased: boolean;
  occupied: boolean;
  property_type: string | null;
  property_type_label: string | null;
  tenure: Tenure;
  disposition: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  neighbourhood: string | null;
  neighbourhood_source: 'lookup' | 'person' | null;
  court_auth_required: boolean;
  lis_pendens_filed: boolean;
  discharged: boolean;
  discharged_on: string | null;
  /** Inactive because the court discharged the client: the third path. */
  client_released: boolean;
  /** Where the geocoder placed it (numeric, sent as text), or null until it has. The map's pins. */
  latitude: string | null;
  longitude: string | null;
}

/** The Real Estate list: the working list, or the record of what is inactive. */
export function useRealEstate(view: RealEstateView, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['properties', view],
    enabled,
    queryFn: () => apiFetch<{ view: RealEstateView; properties: RealEstateRow[] }>(`/properties?view=${view}`, getToken),
    ...queryDefaults,
  });
}

/** One property's full record, for the modal. Money fields are not sent. */
export interface PropertyRecord {
  id: string;
  client_id: string;
  client_ref: string;
  display_name: string;
  legal_status: string;
  date_of_death: string | null;
  client_deceased: boolean;
  occupied: boolean;
  lived_there_while_involved: boolean;
  property_type: string | null;
  property_type_label: string | null;
  tenure: Tenure;
  disposition: string | null;
  street_address: string | null;
  unit: string | null;
  city: string | null;
  county: string | null;
  state: string | null;
  zip: string | null;
  neighbourhood: string | null;
  neighbourhood_source: 'lookup' | 'person' | null;
  parcel_id: string | null;
  legal_description: string | null;
  lis_pendens_filed: boolean;
  court_auth_required: boolean;
  notes: string | null;
  discharged: boolean;
  discharged_on: string | null;
  created_at: string;
  is_migrated: boolean;
  client_released: boolean;
  held_since: string | null;
  held_since_source: 'person' | 'load' | null;
}

/** The property's initial site visit deadline (122): owed per property. */
export interface SiteVisitDeadline {
  id: string;
  label: string;
  status: string;
  due_date: string;
  anchor_date: string | null;
  completed_on: string | null;
  days_taken: number | null;
  /** Withheld (null) for everyone but the System Administrator and the CPO (A-#86 B1). */
  is_provisional: boolean | null;
  closed_by_event: string | null;
  met_by_property_visit_id: string | null;
  /** P-57: the day the client stopped living here, which started this row; null for a row no leaving started. */
  property_left_on: string | null;
  days_until: number;
}

/** A site visit to the property (122): current version; a withdrawn one is kept and marked. */
export interface SiteVisit {
  id: string;
  visited_on: string;
  note: string | null;
  withdrawn: boolean;
  current_version: number;
  source: 'app' | 'load';
  created_at: string;
  visited_by: string | null;
  visited_by_user_id: string | null;
  /** Documents were retrieved on the visit (151, A-#154). */
  documents_retrieved: boolean;
  /** Time spent on the visit, in minutes; null where not recorded. */
  duration_minutes: number | null;
  /** How many times it has been corrected (125); a withdrawal is not an edit. */
  edits: number;
  recorded_by: string | null;
  withdrawal_reason: string | null;
  withdrawn_at: string | null;
}

/** One disposition in the history (094). Append-only; a mistake is withdrawn, never removed. */
export interface DispositionEvent {
  id: string;
  disposition: string;
  effective_on: string | null;
  effective_date_unknown: boolean;
  source: 'Recorded' | 'Before history' | 'Written directly' | 'Imported';
  notes: string | null;
  created_at: string;
  recorded_by: string | null;
  withdrawn_by_event_id: string | null;
  withdrawn_because: string | null;
  withdrawn_at: string | null;
}

export interface PropertyDetail {
  may_record: boolean;
  property: PropertyRecord;
  history: DispositionEvent[];
  dispositions: string[];
  /** The dispositions that make the property inactive (096): Sold, Surrendered, and since 122 Life estate ended, Ended, other, and after a death Inherited by Family and Transferred to Public Administrator. */
  endings: string[];
  needs_court_authority: string[];
  site_visits: SiteVisitDeadline[];
  visits: SiteVisit[];
  /** Active staff who may be named as the visitor; empty for a reader who may not record. */
  visitors: { id: string; full_name: string; is_me: boolean }[];
  /** 150: whether this reader may record a site visit here: property at write, or the Property team member holding
   *  this property's open site-visit task (may_record_site_visit(), the database's one rule). */
  may_record_site_visit: boolean;
}

export interface InactivePreview {
  id: string;
  discharged: boolean;
  disposition: string | null;
  court_auth_required: boolean;
  placed_here: boolean;
  lived_there_while_involved: boolean;
  past_address_exists: boolean;
  holds_another: boolean;
  site_visits_open: number;
}

/** What recording a disposition did, counted after. */
export interface DispositionDone {
  disposition: string | null;
  inactive: boolean;
  past_address_added: boolean;
  site_visits_closed: number;
}

export function useProperty(id: string | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['property', id],
    queryFn: () => apiFetch<PropertyDetail>(`/properties/${id}`, getToken),
    enabled: id !== null,
    ...queryDefaults,
  });
}

/** Everything a property change can move: the record, both lists, the client page. */
function usePropertyChanged(id: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['property', id] });
    void queryClient.invalidateQueries({ queryKey: ['properties'] });
    void queryClient.invalidateQueries({ queryKey: ['client'] });
  };
}

function post<T>(getToken: () => Promise<string>, path: string, body: unknown) {
  return apiFetch<T>(path, getToken, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function useRecordDisposition(id: string) {
  const { getToken } = useSession();
  const changed = usePropertyChanged(id);
  return useMutation({
    mutationFn: (body: { disposition: string; effective_on?: string; date_unknown: boolean; notes?: string; confirm_inactive?: boolean }) =>
      post<DispositionDone>(getToken, `/properties/${id}/dispositions`, body),
    onSuccess: changed,
  });
}

export function useRecordSiteVisit(id: string) {
  const { getToken } = useSession();
  const changed = usePropertyChanged(id);
  return useMutation({
    // duration_minutes: A-#83 A7 (contract v4), optional, 1-1440, as on welfare visits.
    // documents_retrieved: 151 (A-#154), unticked unless said; a first visit with it makes the Finance follow-up.
    mutationFn: (body: { visited_on: string; visited_by_user_id: string; note?: string; duration_minutes?: number;
                         documents_retrieved?: boolean }) =>
      post<{ visit_id: string; site_visits: SiteVisitDeadline[] }>(getToken, `/properties/${id}/visits`, body),
    onSuccess: changed,
  });
}

export function useWithdrawSiteVisit(propertyId: string) {
  const { getToken } = useSession();
  const changed = usePropertyChanged(propertyId);
  return useMutation({
    mutationFn: ({ visitId, reason }: { visitId: string; reason: string }) =>
      post<{ site_visits: SiteVisitDeadline[] }>(getToken, `/property-visits/${visitId}/withdraw`, { reason }),
    onSuccess: changed,
  });
}

export function useWithdrawDisposition(id: string) {
  const { getToken } = useSession();
  const changed = usePropertyChanged(id);
  return useMutation({
    mutationFn: ({ eventId, reason }: { eventId: string; reason: string }) =>
      post<{ disposition: string | null }>(getToken, `/properties/${id}/dispositions/${eventId}/withdraw`, { reason }),
    onSuccess: changed,
  });
}

export function useInactivePreview(id: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['property', id, 'inactive-preview'],
    queryFn: () => apiFetch<{ preview: InactivePreview }>(`/properties/${id}/inactive-preview`, getToken),
    enabled,
    ...queryDefaults,
  });
}
